import { useTranslation } from 'react-i18next'
import { RichText } from './RichText'
import { renderInline } from './richInline'
import { fmt, fmtWeekday } from './planFormat'

// VH-229 refinement: the card building blocks of the weekly plan — the Mon–Fri daily menu cards
// (ingredients, nutrient chips, why line) and the weekend proposal cards with a max calorie budget.

const SLOTS = ['breakfast', 'lunch', 'afternoon', 'dinner', 'dessert']
const SEVERITY_BAR = { ok: 'border-l-[#2C6E49]', warn: 'border-l-[#9C6511]', crit: 'border-l-[#A6362B]' }
const GRID = 'grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'

export function PlanCard({ label, accent = false, children, testId }) {
  return (
    <div className={`bg-white rounded border ${accent ? 'border-2 border-[#0E6B60]' : 'border-[#D8DCD5]'} px-4 py-4 flex flex-col gap-2.5 font-sans text-[#14181A]`} data-testid={testId}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#0E6B60]">{label}</span>
      {children}
    </div>
  )
}

export function PlanLines({ rows, quantityKey = 'amount' }) {
  return (
    <ul className="flex flex-col">
      {rows.map((row, i) => (
        <li key={i} className={`flex justify-between gap-3 py-1 border-b border-dotted border-[#D8DCD5] last:border-b-0 text-[15px] font-sans ${row.owned ? 'text-[#8B959C]' : ''}`}>
          <span>{renderInline(row.name)}</span>
          <span className="font-mono text-[13px] text-[#5B6670] text-right shrink-0 max-w-[45%]">{row[quantityKey] || '—'}</span>
        </li>
      ))}
    </ul>
  )
}

export function PlanMacros({ totals, lang, signed = false }) {
  const { t } = useTranslation()
  if (!totals) return null
  const sign = signed ? '+' : ''
  const chips = [
    ['kcal', t('weeklyPlan.macro.kcal'), false],
    ['protein_g', t('weeklyPlan.macro.protein'), true],
    ['carbs_g', t('weeklyPlan.macro.carbs'), false],
    ['fiber_g', t('weeklyPlan.macro.fiber'), true],
  ].filter(([key]) => totals[key] !== undefined && totals[key] !== null && (key !== 'fiber_g' || totals[key] > 0))
  return (
    <div className="flex flex-wrap gap-1.5 mt-0.5">
      {chips.map(([key, unit, hi]) => (
        <span key={key} className={`font-mono text-[11.5px] px-1.5 py-0.5 rounded-sm ${hi ? 'bg-[#DFEBE2] text-[#2C6E49] font-medium' : 'bg-[#ECEEE9] text-[#5B6670]'}`}>
          {sign}{fmt(totals[key], lang)} {unit}
        </span>
      ))}
    </div>
  )
}

/** Mon–Fri: one card per meal slot plus the training-day addition. */
export function PlanMenuCards({ document: doc }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const meals = (doc?.daily_menu?.meals ?? []).slice().sort((a, b) => SLOTS.indexOf(a.slot) - SLOTS.indexOf(b.slot))
  const training = doc?.training_day ?? {}
  if (meals.length === 0) return null
  return (
    <div className={GRID} data-testid="plan-menu-cards">
      {meals.map((meal) => (
        <PlanCard key={meal.slot} testId={`plan-meal-${meal.slot}`}
          label={`${t(`weeklyPlan.slot.${meal.slot}`, meal.slot)}${meal.note ? ` · ${meal.note}` : ''}`}>
          <PlanLines rows={meal.ingredients ?? []} />
          <PlanMacros totals={meal.totals} lang={lang} />
          {meal.why && <RichText text={meal.why} paragraphClassName="text-[13.5px] text-[#5B6670] font-sans" />}
        </PlanCard>
      ))}
      {(training.ingredients ?? []).length > 0 && (
        <PlanCard accent label={t('weeklyPlan.menu.trainingOnly')} testId="plan-training-day">
          <PlanLines rows={training.ingredients} />
          <PlanMacros totals={training.totals} lang={lang} signed />
          {training.reason && <RichText text={training.reason} paragraphClassName="text-[13.5px] text-[#5B6670] font-sans" />}
        </PlanCard>
      )}
    </div>
  )
}

function Proposal({ row, lang }) {
  return (
    <li className={`border-l-[3px] ${SEVERITY_BAR[row.severity] ?? 'border-l-[#D8DCD5]'} pl-3 py-1 flex flex-col gap-1`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8B959C]">{renderInline(row.when)}</span>
      {row.what && <span className="text-[15px]">{renderInline(row.what)}</span>}
      {(row.ingredients ?? []).length > 0 && <PlanLines rows={row.ingredients} />}
      {row.totals && <PlanMacros totals={row.totals} lang={lang} />}
      {row.why && <span className="text-[13.5px] text-[#5B6670]">{renderInline(row.why)}</span>}
    </li>
  )
}

/**
 * Saturday and Sunday as proposal cards with the day's max calories from the frozen targets.
 * Rows of other days (e.g. Friday evening) come first as their own card.
 */
export function PlanWeekendCards({ document: doc }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const rows = doc?.weekend?.rows ?? []
  const days = doc?.weekend?.days ?? []
  const other = (doc?.weekend?.other_rows ?? []).map((i) => rows[i]).filter(Boolean)
  if (days.length === 0 && rows.length === 0) return null
  return (
    <div className={GRID} data-testid="plan-weekend-cards">
      {other.length > 0 && (
        <PlanCard label={t('weeklyPlan.weekend.before')} testId="plan-weekend-other">
          <ul className="flex flex-col gap-3">{other.map((row, i) => <Proposal key={i} row={row} lang={lang} />)}</ul>
        </PlanCard>
      )}
      {days.map((day) => {
        const proposals = (day.row_indices ?? []).map((i) => rows[i]).filter(Boolean)
        return (
          <PlanCard key={day.day} testId={`plan-weekend-${day.day}`} accent={day.training_day}
            label={`${fmtWeekday(day.date, lang)}${day.training_day ? ` · ${t('weeklyPlan.weekend.trainingDay')}` : ''}`}>
            {day.budget_kcal != null && (
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="font-mono text-[17px] font-medium tabular-nums whitespace-nowrap" data-testid={`plan-weekend-${day.day}-budget`}>
                  {t('weeklyPlan.weekend.maxKcal', { value: fmt(day.budget_kcal, lang) })}
                </span>
                {day.protein_target_g != null && (
                  <span className="font-mono text-[11.5px] px-1.5 py-0.5 rounded-sm bg-[#DFEBE2] text-[#2C6E49] font-medium">
                    {t('weeklyPlan.weekend.proteinTarget', { value: fmt(day.protein_target_g, lang) })}
                  </span>
                )}
              </div>
            )}
            {day.remaining_kcal != null && (
              <p className={`text-[13px] font-mono ${day.remaining_kcal < 0 ? 'text-[#A6362B]' : 'text-[#2C6E49]'}`}>
                {t('weeklyPlan.weekend.remaining', { planned: fmt(day.planned_kcal, lang), remaining: fmt(day.remaining_kcal, lang) })}
              </p>
            )}
            {proposals.length > 0 ? (
              <ul className="flex flex-col gap-3">{proposals.map((row, i) => <Proposal key={i} row={row} lang={lang} />)}</ul>
            ) : (
              <p className="text-[13.5px] text-[#5B6670]">{t('weeklyPlan.weekend.freeDay')}</p>
            )}
          </PlanCard>
        )
      })}
    </div>
  )
}
